import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ApiRequestError } from '../api/client';
import { api } from '../api/shop';
import { usePageTitle } from '../a11y/usePageTitle';
import { useAuth } from '../auth/AuthContext';
import type { OtpChallenge } from '../api/types';

/**
 * Three ways in, and the third is what makes the other two safe to have.
 *
 *   PASSWORD    the everyday path once an account exists.
 *   CREATE      name, birthday, email, password. Creates nothing on submit — the
 *               server holds the details against an emailed code, so an account only
 *               ever appears on an address somebody has proved they can read.
 *   CODE        the recovery path, and the whole reason there is no "forgot
 *               password" screen. A code sent to the address on the account already
 *               proves exactly what a reset link would prove, in one step instead of
 *               three, with no reset token to leak or expire badly.
 *
 * The code step is shared: both CREATE and CODE end there, and the server's message
 * is shown verbatim because it is identical whether or not the account exists. This
 * page must never become a way to find out who shops here.
 */

/** Long enough to outlast normal mail delivery, short enough not to feel punitive. */
const RESEND_SECONDS = 30;

/** Mirrors PasswordPolicy.MinimumLength on the server. Shown, not silently enforced. */
const MIN_PASSWORD_LENGTH = 12;

/** Mirrors ConsumerSignUpRequest.MinimumAgeYears. */
const MINIMUM_AGE_YEARS = 13;

type Mode = 'password' | 'signup' | 'code';

/**
 * The latest birthday that clears the age gate, as YYYY-MM-DD for the date input's
 * `max`. Stops the picker offering dates the server is about to reject — the
 * server still checks, because a `max` attribute is a courtesy, not a control.
 */
function latestAllowedBirthday(): string {
  const today = new Date();
  const cutoff = new Date(
    today.getFullYear() - MINIMUM_AGE_YEARS,
    today.getMonth(),
    today.getDate(),
  );

  // Built from the local parts rather than toISOString(), which converts to UTC and
  // shifts the date for anyone west of Greenwich.
  const month = `${cutoff.getMonth() + 1}`.padStart(2, '0');
  const day = `${cutoff.getDate()}`.padStart(2, '0');
  return `${cutoff.getFullYear()}-${month}-${day}`;
}

export function SignIn() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { signIn } = useAuth();

  // Lands on the dashboard rather than the bare voucher list: it is the page that
  // answers "what do I have?" for a shopper who has just come back.
  const next = params.get('next') ?? '/account';

  const [mode, setMode] = useState<Mode>('password');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');

  const [code, setCode] = useState('');
  const [challenge, setChallenge] = useState<OtpChallenge | null>(null);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /*
   * RESEND COOLDOWN.
   *
   * Two reasons, and the second is the important one. A customer whose code is slow
   * will press resend repeatedly, and each press INVALIDATES the code already in
   * flight — so hammering the button is the reliable way to never be able to sign in.
   * It also stops this page being a free way to send mail to an arbitrary address as
   * fast as a script can click.
   *
   * Client-side only, and deliberately not the real defence: the server rate limits
   * too. This one exists so the customer sees why they are waiting.
   */
  const [cooldown, setCooldown] = useState(0);
  const [resent, setResent] = useState(false);

  usePageTitle(
    challenge !== null ? 'Enter your code' : mode === 'signup' ? 'Create an account' : 'Sign in',
  );

  useEffect(() => {
    if (cooldown <= 0) return;

    const timer = window.setInterval(() => {
      setCooldown((seconds) => Math.max(0, seconds - 1));
    }, 1000);

    return () => window.clearInterval(timer);
  }, [cooldown > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  function switchTo(target: Mode) {
    setMode(target);
    setError(null);
    // The password is dropped on every switch. A value left in state is a value that
    // ends up posted by whichever form the customer submits next.
    setPassword('');
    setCode('');
  }

  async function submitPassword(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);

    try {
      signIn(await api.signInWithPassword(email.trim(), password));
      navigate(next, { replace: true });
    } catch (ex) {
      setError(
        ex instanceof ApiRequestError ? ex.message : 'Something went wrong. Please try again.',
      );
    } finally {
      setBusy(false);
    }
  }

  async function submitSignUp(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);

    try {
      setChallenge(
        await api.signUp({
          email: email.trim(),
          fullName: fullName.trim(),
          dateOfBirth,
          password,
        }),
      );

      // Cleared the moment it has been sent. It is about to be irrelevant — the code
      // step is what finishes the sign-up — and there is no reason to keep it in
      // memory while the customer reads their inbox.
      setPassword('');
      setCooldown(RESEND_SECONDS);
    } catch (ex) {
      setError(
        ex instanceof ApiRequestError ? ex.message : 'Something went wrong. Please try again.',
      );
    } finally {
      setBusy(false);
    }
  }

  async function requestCode(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);

    try {
      setChallenge(await api.startSignIn({ email: email.trim() }));
      setCooldown(RESEND_SECONDS);
    } catch (ex) {
      setError(ex instanceof ApiRequestError ? ex.message : 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  async function resendCode() {
    setBusy(true);
    setError(null);
    setResent(false);

    try {
      /*
       * ALWAYS the plain code endpoint, even when this started as a sign-up.
       *
       * Re-posting the sign-up would re-hash the password we have already cleared,
       * and the account is created from the details on the ORIGINAL challenge — so a
       * resend that replaced it would quietly discard the name and birthday the
       * customer typed. Instead the resend signs them in, and they are prompted to
       * add a password from their account page.
       */
      setChallenge(await api.startSignIn({ email: email.trim() }));
      setCode('');
      setCooldown(RESEND_SECONDS);
      setResent(true);
    } catch (ex) {
      setError(
        ex instanceof ApiRequestError ? ex.message : 'Could not send another code. Please try again.',
      );
    } finally {
      setBusy(false);
    }
  }

  async function submitCode(event: React.FormEvent) {
    event.preventDefault();
    if (challenge === null) return;

    setBusy(true);
    setError(null);

    try {
      signIn(await api.verifySignIn(challenge.challengeId, code.trim()));
      navigate(next, { replace: true });
    } catch (ex) {
      setError(ex instanceof ApiRequestError ? ex.message : 'That code did not work. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  /* ------------------------------------------------------------- code step --- */

  if (challenge !== null) {
    return (
      <div className="panel stack narrow gap-lg" style={{ margin: '48px auto' }}>
        <div className="stack" style={{ gap: 6 }}>
          <h1 style={{ fontSize: 26 }}>Enter your code</h1>
          {/* The API's message is identical whether or not the account exists.
              Shown verbatim so this page never becomes a way to test addresses. */}
          <p className="muted">{challenge.message}</p>
        </div>

        <form className="stack" style={{ gap: 16 }} onSubmit={(e) => void submitCode(e)}>
          <div className="field">
            <label htmlFor="code">{challenge.codeLength}-digit code</label>
            <input
              id="code"
              type="text"
              inputMode="numeric"
              /* Lets the phone offer the code straight from the message. */
              autoComplete="one-time-code"
              pattern="[0-9]*"
              maxLength={challenge.codeLength}
              required
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              className="code-input"
              /* The expiry is part of the field's instructions, not a caption
                 floating near it. Without aria-describedby a screen reader never
                 reads it, and "why did my code stop working" becomes a support
                 conversation. */
              aria-describedby="code-hint"
              /* Announces the field itself as rejected, not just the message. */
              aria-invalid={error !== null}
            />
            <span className="hint" id="code-hint">
              Expires in {Math.round(challenge.expiresInSeconds / 60)} minutes.
            </span>
          </div>

          {error !== null && (
            <p className="notice error" role="alert">
              {error}
            </p>
          )}

          <button
            type="submit"
            className="btn block"
            disabled={busy || code.length !== challenge.codeLength}
          >
            {busy ? 'Checking…' : 'Continue'}
          </button>

          {/* role="status" so the confirmation is announced. Without it the only
              feedback for a screen-reader user is a button that quietly becomes
              disabled. */}
          {resent && (
            <p className="hint" role="status">
              A new code is on its way. The previous one no longer works.
            </p>
          )}

          <button
            type="button"
            className="btn secondary block"
            onClick={() => void resendCode()}
            disabled={busy || cooldown > 0}
          >
            {cooldown > 0 ? `Send a new code in ${cooldown}s` : 'Send a new code'}
          </button>

          <button
            type="button"
            className="btn secondary block"
            onClick={() => {
              setChallenge(null);
              setCode('');
              setError(null);
              setResent(false);
              setCooldown(0);
            }}
          >
            Start again
          </button>
        </form>
      </div>
    );
  }

  /* ------------------------------------------------------------ create step --- */

  if (mode === 'signup') {
    return (
      <div className="panel stack narrow gap-lg" style={{ margin: '48px auto' }}>
        <div className="stack" style={{ gap: 6 }}>
          <h1 style={{ fontSize: 26 }}>Create an account</h1>
          <p className="muted">We will email you a code to confirm your address.</p>
        </div>

        <form className="stack" style={{ gap: 16 }} onSubmit={(e) => void submitSignUp(e)}>
          <div className="field">
            <label htmlFor="fullName">Full name</label>
            <input
              id="fullName"
              type="text"
              autoComplete="name"
              required
              minLength={2}
              maxLength={150}
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="signup-email">Email address</label>
            <input
              id="signup-email"
              type="email"
              inputMode="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
            />
          </div>

          <div className="field">
            <label htmlFor="dob">Date of birth</label>
            <input
              id="dob"
              type="date"
              autoComplete="bday"
              required
              max={latestAllowedBirthday()}
              value={dateOfBirth}
              onChange={(e) => setDateOfBirth(e.target.value)}
              aria-describedby="dob-hint"
            />
            <span className="hint" id="dob-hint">
              So we can send you something on your birthday. You need to be at least{' '}
              {MINIMUM_AGE_YEARS}.
            </span>
          </div>

          <div className="field">
            <label htmlFor="new-password">Password</label>
            <input
              id="new-password"
              type="password"
              /* new-password, not password: this is what tells a password manager to
                 offer to generate and save one rather than autofill an old one. */
              autoComplete="new-password"
              required
              minLength={MIN_PASSWORD_LENGTH}
              maxLength={200}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              aria-describedby="password-hint"
            />
            <span className="hint" id="password-hint">
              At least {MIN_PASSWORD_LENGTH} characters. Length matters more than
              symbols — a few ordinary words work well.
            </span>
          </div>

          {error !== null && (
            <p className="notice error" role="alert">
              {error}
            </p>
          )}

          <button
            type="submit"
            className="btn block"
            disabled={
              busy
              || email.trim() === ''
              || fullName.trim().length < 2
              || dateOfBirth === ''
              || password.length < MIN_PASSWORD_LENGTH
            }
          >
            {busy ? 'Sending…' : 'Create account'}
          </button>

          <button type="button" className="btn secondary block" onClick={() => switchTo('password')}>
            I already have an account
          </button>
        </form>
      </div>
    );
  }

  /* ------------------------------------------------------- code-only step --- */

  if (mode === 'code') {
    return (
      <div className="panel stack narrow gap-lg" style={{ margin: '48px auto' }}>
        <div className="stack" style={{ gap: 6 }}>
          <h1 style={{ fontSize: 26 }}>Sign in with a code</h1>
          <p className="muted">
            We will email you a code instead. Use this if you have forgotten your
            password — you can set a new one once you are in.
          </p>
        </div>

        <form className="stack" style={{ gap: 16 }} onSubmit={(e) => void requestCode(e)}>
          <div className="field">
            <label htmlFor="code-email">Email address</label>
            <input
              id="code-email"
              type="email"
              inputMode="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
            />
          </div>

          {error !== null && (
            <p className="notice error" role="alert">
              {error}
            </p>
          )}

          <button type="submit" className="btn block" disabled={busy || email.trim() === ''}>
            {busy ? 'Sending…' : 'Email me a code'}
          </button>

          <button type="button" className="btn secondary block" onClick={() => switchTo('password')}>
            Use my password instead
          </button>
        </form>
      </div>
    );
  }

  /* -------------------------------------------------------- password step --- */

  return (
    <div className="panel stack narrow gap-lg" style={{ margin: '48px auto' }}>
      <div className="stack" style={{ gap: 6 }}>
        <h1 style={{ fontSize: 26 }}>Sign in</h1>
        <p className="muted">Welcome back.</p>
      </div>

      <form className="stack" style={{ gap: 16 }} onSubmit={(e) => void submitPassword(e)}>
        <div className="field">
          <label htmlFor="email">Email address</label>
          <input
            id="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
          />
        </div>

        <div className="field">
          <label htmlFor="password">Password</label>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            aria-invalid={error !== null}
          />
        </div>

        {error !== null && (
          <p className="notice error" role="alert">
            {error}
          </p>
        )}

        <button
          type="submit"
          className="btn block"
          disabled={busy || email.trim() === '' || password === ''}
        >
          {busy ? 'Signing in…' : 'Sign in'}
        </button>

        {/* No "forgot password" link, because there is nothing to reset: a code sent
            to the address on the account proves the same thing a reset link would,
            in one step, with no reset token to leak. */}
        <button type="button" className="btn secondary block" onClick={() => switchTo('code')}>
          Email me a code instead
        </button>

        <button type="button" className="btn secondary block" onClick={() => switchTo('signup')}>
          Create an account
        </button>
      </form>
    </div>
  );
}
